import { Injectable } from '@angular/core';
import {
  HttpInterceptor,
  HttpRequest,
  HttpHandler,
  HttpEvent,
  HttpErrorResponse
} from '@angular/common/http';
import { Observable, from, of, throwError } from 'rxjs';
import { catchError, switchMap, tap } from 'rxjs/operators';
import { API_URLS } from '../config/api-urls';

/**
 * Auth Interceptor
 *
 * 1. Attaches `withCredentials: true` to all requests going to our own gateway,
 *    so the browser sends the session cookie (JSESSIONID) for OAuth2 session auth.
 *
 * 2. Reads the `XSRF-TOKEN` cookie set by Spring's CookieCsrfTokenRepository and
 *    attaches it as the `X-XSRF-TOKEN` request header on all state-changing methods
 *    (POST, PUT, PATCH, DELETE).  Spring validates the header against the cookie
 *    value on every state-changing request.
 *
 * 3. Redirects to the gateway login page on 401/403.
 */
@Injectable()
export class AuthInterceptor implements HttpInterceptor {

  // Cached access token — fetched from the gateway's /api/token endpoint
  // after login. Spring Cloud Gateway MVC's TokenRelay= filter is unreliable
  // for session-authenticated callers, so we grab the token once and attach
  // it as Authorization: Bearer on every downstream API call.
  private cachedAccessToken: string | null = null;
  private tokenFetchInFlight: Promise<string | null> | null = null;

  intercept(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    const isOwnApi = this.isOwnApiRequest(req.url);

    if (!isOwnApi) {
      // Do not attach credentials or CSRF tokens to third-party requests.
      return next.handle(req);
    }

    // Never intercept the /api/token endpoint itself (would infinite-loop).
    const isTokenEndpoint = req.url.endsWith('/api/token');

    return from(isTokenEndpoint ? Promise.resolve(null) : this.ensureAccessToken()).pipe(
      switchMap(token => {
        const STATE_CHANGING = ['POST', 'PUT', 'PATCH', 'DELETE'];
        const needsCsrf = STATE_CHANGING.includes(req.method.toUpperCase());

        let headers = req.headers.set('X-Requested-With', 'XMLHttpRequest');
        if (token && !req.headers.has('Authorization')) {
          headers = headers.set('Authorization', `Bearer ${token}`);
        }
        if (needsCsrf) {
          const csrfToken = this.readCookie('XSRF-TOKEN');
          if (csrfToken) headers = headers.set('X-XSRF-TOKEN', csrfToken);
        }

        const authReq = req.clone({ withCredentials: true, headers });

        return next.handle(authReq).pipe(
          catchError((error: HttpErrorResponse) => {
            if (error.status === 401 || error.status === 403) {
              // Drop cached token; next request will re-fetch.
              this.cachedAccessToken = null;
              // Only redirect to /login on truly protected pages. Public
              // pages (customer QR flow, register, home, bar list) render
              // their own empty/error state and should not be booted out.
              const PUBLIC_ROUTES = ['/', '/customer', '/register', '/register-bar', '/bars', '/forgot-password', '/login'];
              if (!PUBLIC_ROUTES.some(p => window.location.pathname === p || window.location.pathname.startsWith(p + '/'))) {
                window.location.href = '/login';
              }
            }
            return throwError(() => error);
          })
        );
      })
    );
  }

  /** Fetch the current session's access token from the gateway. Cached. */
  private ensureAccessToken(): Promise<string | null> {
    if (this.cachedAccessToken) return Promise.resolve(this.cachedAccessToken);
    if (this.tokenFetchInFlight) return this.tokenFetchInFlight;
    this.tokenFetchInFlight = fetch(`${API_URLS.GATEWAY}/api/token`, { credentials: 'include' })
      .then(r => r.ok ? r.json() : null)
      .then(body => {
        this.cachedAccessToken = body?.access_token ?? null;
        this.tokenFetchInFlight = null;
        return this.cachedAccessToken;
      })
      .catch(() => { this.tokenFetchInFlight = null; return null; });
    return this.tokenFetchInFlight;
  }

  /**
   * Returns true only when the URL targets our own API gateway.
   * Fixed operator-precedence bug from the previous implementation:
   * the old ternary was evaluated as (A || B) ? C : D which incorrectly
   * marked any absolute URL not matching the gateway host as non-API.
   */
  private isOwnApiRequest(url: string): boolean {
    if (!url) return false;
    // Relative URL (no scheme) — always our own app
    if (!url.startsWith('http')) return true;
    // Absolute URL — compare hostnames exactly, not as a substring.
    // Using url.includes(gatewayHost) was fragile: e.g. the string "api.bar.local"
    // would match inside "https://evil-api.bar.local/steal".  New URL() ensures we
    // compare only the hostname component.
    try {
      const requestHost  = new URL(url).hostname;
      const gatewayHost  = new URL(API_URLS.GATEWAY).hostname;
      return requestHost === gatewayHost;
    } catch {
      return false;
    }
  }

  private readCookie(name: string): string | null {
    const value = `; ${document.cookie}`;
    const parts = value.split(`; ${name}=`);
    if (parts.length === 2) {
      return parts.pop()?.split(';').shift() ?? null;
    }
    return null;
  }
}
